import { BlockReferenceArray } from '@/components/cms/types/block';
import { BlockComponentModel } from '@/components/cms/types/blok-component';

import ImageStrapi, {
  ImageStrapiProps,
} from '@/decodo/components/atoms/Image/ImageStrapi';
import { LinkButtonStrapiProps } from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi, {
  RichTextFieldStrapiProps,
} from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { AuthorStrapiProps } from '@/decodo/components/organisms/author/AuthorStrapi';
import { PostTopic } from '@/decodo/features/shared//BlogMetaData/PostTags';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContentDC';

import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';
import { BlogOverview, BlogOverviewProps } from '.';

export type MetadataStrapiProps = {
  slug: string;
  metadata: {
    image: ImageStrapiProps;
    releaseDate: string;
    timeSpentReading: number;
    newUntil?: string;
    title: string;
    description: RichTextFieldStrapiProps;
    topics: BlockReferenceArray<PostTopic>;
    author: AuthorStrapiProps['blogAuthor'];
  };
};
export interface BlogOverviewStrapiProps {
  articles: BlockReferenceArray<MetadataStrapiProps>;
  header?: BlockHeader;
  link?: LinkButtonStrapiProps;
}

export function BlogOverviewStrapi({
  block,
  ...rest
}: BlockComponentModel<BlogOverviewStrapiProps>) {
  const { header, articles } = block;

  const formattedData: BlogOverviewProps['items'] =
    articles?.data?.map((article) => {
      const {
        author,
        description,
        image,
        newUntil,
        releaseDate,
        timeSpentReading,
        title,
        topics,
      } = article.attributes.metadata;
      const newDate = newUntil ? new Date(newUntil) : new Date();

      return {
        slug: article.attributes.slug,
        image: (
          <ImageStrapi
            block={{
              ...image,
            }}
            inner={{
              classNames: {
                container: 'overflow-hidden rounded-xl',
                image: '!object-cover min-h-[200px]',
              },
              sizes: '200px',
            }}
            width={200}
            height={200}
          />
        ),
        releaseDate: releaseDate,
        timeSpentReading: timeSpentReading,
        herotitle: title,
        description: (
          <RichTextFieldStrapi
            block={description}
            classNames={{ link: { root: 'hover:no-underline text-inherit' } }}
          />
        ),
        topics:
          topics.data?.map((topic) => ({
            topic: topic.attributes.topic,
            slug: topic.attributes.slug,
          })) ?? [],
        isNew: newDate > new Date(),
        author: author?.data
          ? {
              image: (
                <ImageStrapi
                  block={{
                    ...author?.data?.attributes.image,
                  }}
                  inner={{ classNames: { image: ' w-9 rounded-full' } }}
                  width={36}
                  height={36}
                />
              ),
              name: author?.data?.attributes.name,
            }
          : undefined,
      };
    }) ?? [];

  return (
    <BlockWrapper {...rest} data-tracker-section={SECTION_NAMES.BLOG_OVERVIEW}>
      <div>
        <BlockHeaderContent {...header}>
          <BlogOverview items={formattedData} />
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
